import {
  AlertDetails,
  JFormik,
  JItem,
  selectUser,
} from '@centerpiece/components';
import {
  Alert,
  Box,
  Container,
  Link,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AccountBox, LinkRounded } from '@mui/icons-material';
import { ContactComponent } from 'components/ContactComponent';
import {
  useFESProjectForm,
  useCustomProjectForm,
  useProjectTemplateForm,
} from 'jformik/useProjectForm';
import {
  variableRoleStakeholder,
  customStakeholder,
  useInternalRolesForm,
} from 'jformik/useStakeholderForm';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useGetSubscriptionContactsQuery,
  useGetOrganizationsQuery,
  useGetProjectQuery,
  useGetStagesQuery,
  usePatchStageMutation,
  useGetProjectStatesQuery,
  useUpdateProjectMutation,
  usePatchProjectMutation,
  useGetProjectContactsQuery,
  useGetRolesQuery,
  useGetSubscribersQuery,
  useDeleteProjectContactMutation,
  useCreateProjectContactMutation,
  useUpdateProjectContactMutation,
  useGetCommentsQuery,
  useCreateCommentMutation,
  usePatchCommentMutation,
} from 'store/api';
import {
  ProjectContact,
  Subscriber,
  Project,
  ProjectState,
  Relationship,
} from 'store/api/models';
import { useTutorialContext } from 'tutorials/tutorial.context';
import { stakeHolderOptions } from 'utils/contacts.utils';
import { useGetWorkFlowForm } from 'utils/workflow.utils';
import * as Yup from 'yup';
import { useProjectContext } from 'contexts/project.context';

export interface ProjectTeamTabProps {
  id: number;
  archived?: boolean;
  previewTab?: boolean;
  template?: boolean;
  exampleProject?: Project;
  afterSave?: Function;
}

export const ProjectTeamTab = (props: ProjectTeamTabProps) => {
  const theme = useTheme();
  const formRef = useRef<any>(null);
  const { role_relationships, currentStage } = useProjectContext();
  const { data: subscription_contacts = [] } = useGetSubscriptionContactsQuery({
    params: { nested: 'all' },
  });
  const { data: organizations = [] } = useGetOrganizationsQuery({
    params: { nested: 'all' },
  });
  const downMd = useMediaQuery(theme.breakpoints.down('md'));
  const {
    id = 0,
    template = false,
    archived = false,
    afterSave = () => {},
    ...rest
  } = props;

  const {
    data: project = props.exampleProject || ({} as Project),
    isLoading: projectLoading,
    isFetching: projectFetching,
  } = useGetProjectQuery(
    template
      ? {
          id: id,
          params: {
            nested: 'details',
            template: template,
            archived: archived ? archived : undefined,
          },
        }
      : {
          id: id,
          params: {
            archived: archived ? archived : undefined,
            nested: 'details',
          },
        },
    { skip: !id || !!props.exampleProject }
  );
  const { data: stages = [] } = useGetStagesQuery(
    { params: { project: id, optimized: true } },
    { skip: !id || !!props.exampleProject }
  );
  const [alertDetails, setAlertDetails] = useState<AlertDetails>();
  const [openContact, setOpenContact] = useState<{
    open: boolean;
    id?: number;
    disabled?: boolean;
    callback?: Function;
    initialValues?: any;
  }>({ open: false });
  const getWorkflowForm = useGetWorkFlowForm({ setOpenContact });
  const workflowForms = stages?.map(s => getWorkflowForm(s)).filter(s => s);
  const [patchStage] = usePatchStageMutation();

  const { data: project_contacts = [], isLoading: projectContactsLoading } =
    useGetProjectContactsQuery(
      { params: { nested: 'all', project: id } },
      { skip: !id || !!props.exampleProject }
    );
  const { data: roles = [] } = useGetRolesQuery({});
  const { data: subscribers = [] } = useGetSubscribersQuery({
    params: { nested: 'all' },
  });
  const [deleteProjectContact] = useDeleteProjectContactMutation();
  const [createProjectContact] = useCreateProjectContactMutation();
  const [editProjectContact] = useUpdateProjectContactMutation();
  const [fesValidation, fesForm] = useFESProjectForm({
    id: id,
    edit: true,
    setOpenContact,
  });
  const [customValidation, customForm] = useCustomProjectForm({
    id: id,
    edit: true,
  });
  const [templateValidation, templateForm] = useProjectTemplateForm({
    id: id,
    edit: true,
  });

  const { state: tutorialState, setState: setTutorialState } =
    useTutorialContext();
  const {
    data: comments = [],
    isLoading: commentsLoading,
    refetch: refetchComments,
  } = useGetCommentsQuery(
    { params: { nested: 'all', project: `=${id}` } },
    { skip: !id }
  );
  const internalRolesForm = useInternalRolesForm(setOpenContact);
  return (
    <Container maxWidth={'xl'}>
      {openContact?.open && (
        <ContactComponent
          close={() => setOpenContact({ open: false })}
          {...openContact}
        />
      )}
      <Box sx={{ display: openContact?.open ? 'none' : 'block' }}>
        {role_relationships
          ?.filter(
            (r: Relationship) =>
              ((!r.contacts || r.contacts?.length === 0) &&
                r.stages?.filter(s => s.id === currentStage?.id).length) ||
              0 > 0
          )
          ?.map(r => (
            <Alert
              severity="warning"
              sx={{ mb: 1 }}
            >{`${r.role_r.name} is not assigned!`}</Alert>
          ))}
        <Box sx={{ pt: 2, maxWidth: '90vw' }}>
          {!template && (
            <JFormik
              enableReinitialize={true}
              initialValues={() =>
                ({
                  // contacts: project_contacts
                  //   ?.filter(
                  //     pc =>
                  //       pc?.role_r?.type !== 'External' &&
                  //       !pc?.form?.stakeholdertype
                  //   )
                  //   ?.map(
                  //     (contact: ProjectContact) =>
                  //       ({
                  //         id: contact.id,
                  //         contact: {
                  //           ...contact.contact_r,
                  //           name: contact?.contact_r?.firstname
                  //             ? `${contact?.contact_r?.firstname} ${contact?.contact_r?.lastname}`
                  //             : contact?.contact_r?.email,
                  //         },
                  //         role: { ...contact.role_r },
                  //       } as any)
                  //   ),
                  contacts: project_contacts
                    ?.filter(
                      pc =>
                        (pc?.role_r?.type !== 'External' ||
                          pc?.form?.stakeholdertype) &&
                        pc.contact
                    )
                    ?.map(
                      (contact: ProjectContact) =>
                        ({
                          id: contact.id,
                          contact: {
                            ...(contact.contact_r || []),
                            contact_r: contact.contact_r,
                            name: contact?.contact_r?.firstname
                              ? `${contact?.contact_r?.firstname} ${contact?.contact_r?.lastname}`
                              : contact?.contact_r?.email,
                            // organization: contact?.organization,
                            // role: contact?.role_r?.name,
                          },
                          role: {
                            ...(contact.role_r || { id: 0, name: 'No role' }),
                          },
                          stakeholdertype: contact?.role_r ||
                            contact?.form?.stakeholdertype || {
                              id: 0,
                              name: 'No role',
                            },
                          dynamic: {
                            ...contact?.form,
                            value: {
                              ...(contact.contact_r || []),
                              name: contact?.contact_r?.firstname
                                ? `${contact?.contact_r?.firstname} ${
                                    contact?.contact_r?.lastname
                                  }${
                                    contact?.contact_r?.email
                                      ? ` - ${contact?.contact_r?.email}`
                                      : ``
                                  }`
                                : contact?.contact_r?.email,
                              organization: contact?.organization,
                              role: contact?.role_r?.name || 'No role',
                            },
                          },
                          organization: contact?.organization,
                          form: contact?.form,
                        } as any)
                    ),
                } as any)
              }
              handleSubmit={async (values, formik) => {}}
              loading={projectContactsLoading}
              validationSchema={Yup.object().shape({
                contacts: Yup.array().of(
                  Yup.object().shape({
                    contact: Yup.object()
                      .nullable()
                      .required('Please select a contact'),
                  })
                ),
              })}
              disabled={true}
              form={[
                {
                  name: 'internal-team-label',
                  label: 'Internal Team',
                  itemType: 'typography',
                  jItemProps: { variant: 'overline' },
                },
                {
                  name: 'internal-team-label-bottom',
                  label: '',
                  itemType: 'divider',
                  gridProps: { sx: { paddingTop: '0px !important', pb: 1 } },
                },
                // {
                //   ...internalRolesForm,
                //   jItemProps: {
                //     ...internalRolesForm.jItemProps,
                //     onCreate: async (values: any) => {
                //       await createProjectContact({
                //         project: id,
                //         contact: values?.contact?.contact_r?.id,
                //         role:
                //           values?.stakeholdertype?.id > 0
                //             ? values?.stakeholdertype?.id
                //             : undefined,
                //       } as ProjectContact);
                //       await afterSave();
                //     },
                //     onEdit: async (values: any) => {
                //       await editProjectContact({
                //         id: values?.id,
                //         project: id,
                //         contact: values?.contact?.contact_r?.id,
                //         role:
                //           values?.stakeholdertype?.id > 0
                //             ? values?.stakeholdertype?.id
                //             : undefined,
                //       } as ProjectContact);
                //       await afterSave();
                //     },
                //     onDelete: async (id: number) => {
                //       await deleteProjectContact(id);
                //       await afterSave();
                //     },
                //   },
                // },
                {
                  name: 'contacts',
                  disabled: true,
                  itemType: 'table_array',
                  label: '',
                  value: project_contacts
                    ?.filter(
                      pc =>
                        pc?.role_r?.type !== 'External' &&
                        !pc?.form?.stakeholdertype
                    )
                    ?.map(
                      //SHOULD THIS BE ASYNC FROM FORM?
                      (contact: ProjectContact) =>
                        ({
                          id: contact.id,
                          contact: {
                            ...contact.contact_r,
                            name: `${contact?.contact_r?.firstname} ${contact?.contact_r?.lastname}`,
                          },
                          role: { ...contact.role_r },
                        } as any)
                    ),
                  tableProps: {
                    onCreate: async (values: any) => {
                      await createProjectContact({
                        project: id,
                        contact: values?.contact?.id,
                        role: values?.role?.id,
                        form: values?.form,
                      } as ProjectContact);
                      await afterSave();
                    },
                    onEdit: async (values: any) => {
                      await editProjectContact({
                        id: values?.id,
                        project: id,
                        contact: values?.contact?.id,
                        role: values?.role?.id,
                        form: values?.form,
                      } as ProjectContact);
                      await afterSave();
                    },
                    onDelete: async (id: number) => {
                      await deleteProjectContact(id);
                      await afterSave();
                    },
                    columns: [
                      {
                        name: 'contact',
                        label: 'Teammate',
                        // disabled: true,
                        // freeSolo: true,
                        itemType: 'autocomplete',
                        jItemProps: {
                          getOptionLabel: (option: any) =>
                            option?.name ? `${option.name}` : `Teamate`,
                          sx: {
                            boxSizing: 'border-box',
                            px: { xs: 0.5 },
                          },
                          renderOption: (props: any, option: any) => {
                            return (
                              <Box component="li" {...props}>
                                <small>
                                  <AccountBox
                                    fontSize="inherit"
                                    sx={{ mr: 1 }}
                                    color="secondary"
                                  />
                                </small>
                                {option.name}
                              </Box>
                            );
                          },
                        },
                        options: values => {
                          return subscribers
                            ?.filter(
                              (s: Subscriber) =>
                                !values?.contacts?.find(
                                  (r: any) => s.contact_r?.id === r.contact?.id
                                )
                            )
                            ?.map((subscriber: Subscriber) => ({
                              id: subscriber?.contact_r.id,
                              email: subscriber?.contact_r?.email,
                              name: subscriber?.contact_r?.firstname
                                ? `${subscriber?.contact_r?.firstname} ${subscriber?.contact_r?.lastname}`
                                : subscriber?.contact_r?.email,
                            }));
                        },
                        gridProps: { xs: 12, sm: 5 },
                      },
                      {
                        name: 'role',
                        label: 'Role',
                        itemType: 'autocomplete',
                        // freeSolo: true,
                        jItemProps: {
                          getOptionLabel: (option: any) =>
                            option?.name ? `${option.name}` : `Role`,
                          sx: {
                            boxSizing: 'border-box',
                            px: { xs: 0.5 },
                          },
                        },
                        options: roles.filter(
                          role => role?.type === 'Internal'
                        ),
                        gridProps: { xs: 10, sm: 5 },
                      },
                      {
                        name: 'form.watcher',
                        label: 'Notifications',
                        itemType: 'checkbox',
                        jItemProps: {
                          tooltip:
                            'User will receive email notifications on item and project completions.',
                          sx: {
                            boxSizing: 'border-box',
                            px: { xs: 0.5 },
                          },
                          padding: 'none',
                          size: 'small',
                        },
                        gridProps: { xs: 1, sm: 1 },
                      },
                    ],
                  },
                  gridProps: { sx: { pt: '0 !important' } },
                  jItemProps: {
                    addLabel: 'Invite others to the project',
                  },
                  device: downMd ? 'mobile' : 'desktop',
                },
              ]}
            />
          )}
        </Box>
        <Box sx={{ pt: 2 }}>
          <JFormik
            enableReinitialize={true}
            initialValues={{
              stakeholders: [
                ...project_contacts
                  ?.filter(
                    pc =>
                      (pc?.role_r?.type === 'External' ||
                        pc?.form?.stakeholdertype) &&
                      pc.contact
                  )
                  ?.map(
                    (contact: ProjectContact) =>
                      ({
                        id: contact.id,
                        contact: {
                          ...(contact.contact_r || []),
                          contact_r: contact.contact_r,
                          name: contact?.contact_r?.firstname
                            ? `${contact?.contact_r?.firstname} ${
                                contact?.contact_r?.lastname
                              }${
                                contact?.contact_r?.email
                                  ? ` - ${contact?.contact_r?.email}`
                                  : ``
                              }`
                            : contact?.contact_r?.email,
                          // organization: contact?.organization,
                          // role: contact?.role_r?.name,
                        },
                        role: { ...contact.role_r },
                        stakeholdertype:
                          contact?.role_r || contact?.form?.stakeholdertype,
                        dynamic: {
                          ...contact?.form,
                          value: {
                            ...(contact.contact_r || []),
                            name: contact?.contact_r?.firstname
                              ? `${contact?.contact_r?.firstname} ${
                                  contact?.contact_r?.lastname
                                }${
                                  contact?.contact_r?.email
                                    ? ` - ${contact?.contact_r?.email}`
                                    : ``
                                }`
                              : contact?.contact_r?.email,
                            organization: contact?.organization,
                            role: contact?.role_r?.name,
                          },
                        },
                        organization: contact?.organization,
                        form: contact?.form,
                      } as any)
                  ),
                ...project_contacts
                  ?.filter(
                    pc =>
                      (pc?.role_r?.type === 'External' ||
                        pc?.form?.stakeholdertype) &&
                      pc.organization
                  )
                  ?.map(
                    (organization: ProjectContact) =>
                      ({
                        ...organization,
                        type: 'organization',
                        contact: {
                          ...organization.organization_r,
                          organization_r: organization.organization_r,
                          role: organization?.role_r?.name,
                          type: 'organization',
                        },
                        role: { ...organization.role_r },
                        stakeholdertype:
                          organization?.role_r ||
                          organization?.form?.stakeholdertype,
                        dynamic: {
                          ...organization?.form,
                          value: {
                            ...organization.organization_r,
                            organization_r: organization.organization_r,
                            role: organization?.role_r?.name,
                          },
                        },
                        form: organization?.form,
                      } as any)
                  ),
              ],
            }}
            handleSubmit={async (values, formik) => {}}
            loading={projectContactsLoading}
            validationSchema={Yup.object()}
            disabled={true}
            form={[
              {
                name: 'stakeholder-fields',
                itemType: 'typography',
                jItemProps: {
                  variant: 'overline',
                  sx: { mb: '0 !important', pb: '0 !important' },
                },
                label: 'Project Stakeholders',
              },
              {
                name: 'stakeholder-fields-bottom',
                label: '',
                itemType: 'divider',
                gridProps: { sx: { paddingTop: '0px !important' } },
              },
              {
                // precondition: (values: any) => !disable_type && values?.type === 'Default',
                name: 'stakeholders',
                itemType: 'fieldarray',
                label: '',
                gridProps: {
                  // sx: { paddingTop: '10px !important' },
                },
                device: downMd ? 'mobile' : 'desktop',
                jItemProps: {
                  addLabel: 'Add other project stakeholders',
                  onCreate: async (values: any) => {
                    if (values?.contact?.type !== 'organization') {
                      await createProjectContact({
                        project: id,
                        contact:
                          values?.contact?.contact ||
                          values?.contact?.contact_r?.id,
                        role: values?.role?.id,
                        form: values?.form,
                      } as ProjectContact);
                    } else {
                      await createProjectContact({
                        project: id,
                        organization: values?.contact?.id,
                        role: values?.role?.id,
                        form: values?.form,
                      } as ProjectContact);
                    }
                    await afterSave();
                  },
                  onEdit: async (values: any) => {
                    if (values?.contact?.type !== 'organization') {
                      await editProjectContact({
                        id: values?.id,
                        project: id,
                        contact: values?.contact?.id,
                        role: values?.role?.id,
                        form: values?.form,
                      } as ProjectContact);
                    } else {
                      await editProjectContact({
                        id: values?.id,
                        project: id,
                        organization: values?.contact?.id,
                        role: values?.role?.id,
                        form: values?.form,
                      } as ProjectContact);
                    }
                    await afterSave();
                  },
                  onDelete: async (id: number, callback: Function) => {
                    try {
                      await deleteProjectContact(id);
                      callback(true);
                      toast.info('Stakeholder deleted successfuly');
                      await afterSave();
                    } catch (error) {
                      toast.info('Unable to delete stakeholder');
                    }
                  },
                },
                array: [
                  // {
                  //   name: '',
                  //   label: '',
                  //   itemType: 'embed',
                  //   embed: ({ value, values }) => {
                  //     console.log(values, value);

                  //     return <div>asfas</div>;
                  //   },
                  //   gridProps: { xs: 1, sm: 1 },
                  // },
                  {
                    precondition: (values: any) =>
                      !values?.stakeholders?.[values.index]?.stakeholdertype,
                    name: 'stakeholdertype',
                    label: 'Type',
                    itemType: 'autocomplete',
                    // freeSolo: true, //REMOVING THE ABILITY TO ADD
                    // jItemProps: {
                    //   freeSolo: true,
                    //   addValue:async (name: any, newValue: any, values: any, formik: FormikHelpers<any>) => {
                    //     const res = await createRole({name: newValue?.inputValue, type: 'External'}).unwrap()
                    //     setTimeout(function () {
                    //       formik.setFieldValue(name, res);
                    //     }, 500);
                    //   },
                    // },
                    options: [
                      ...roles?.filter(r => r.type === 'External'),
                      // { id: -1, name: 'Custom' },
                    ],
                  },
                  ...(roles
                    ?.filter(r => r.type === 'External')
                    .map(
                      role =>
                        variableRoleStakeholder(
                          role,
                          stakeHolderOptions({
                            subscription_contacts,
                            organizations,
                            role,
                          }),
                          setOpenContact
                        ) as JItem
                    ) || []),
                  customStakeholder(
                    stakeHolderOptions({
                      subscription_contacts,
                      organizations,
                    }),
                    setOpenContact
                  ) as JItem,
                  {
                    name: 'form.watcher',
                    label: 'Notifications',
                    itemType: 'checkbox',
                    jItemProps: {
                      tooltip:
                        'User will receive email notifications on item and project completions.',
                      sx: {
                        boxSizing: 'border-box',
                        px: { xs: 0.5 },
                      },
                      padding: 'none',
                      size: 'small',
                    },
                    gridProps: { xs: 1, sm: 1 },
                  },
                ],
              },
            ]}
          />
        </Box>
        {workflowForms && workflowForms.length > 0 && (
          <Box sx={{ pt: 2 }}>
            <JFormik
              disabled={true}
              initialValues={{}}
              validationSchema={Yup.object()}
              form={[
                {
                  name: 'workflow-label',
                  label: 'Project Workflow(s)',
                  itemType: 'typography',
                  jItemProps: {
                    variant: 'overline',
                    sx: {
                      fontWeight: 'bold',
                    },
                  },
                },
                {
                  name: 'workflow-label-bottom',
                  label: '',
                  itemType: 'divider',
                  gridProps: {
                    sx: {
                      paddingTop: '0px !important',
                      paddingBottom: '16px',
                    },
                  },
                },
              ]}
            />
            {workflowForms?.map(workflowForm => (
              <JFormik
                disabled={true}
                formRef={formRef}
                editable
                initialValues={workflowForm?.initialValues || {}}
                validationSchema={workflowForm?.validationSchema}
                handleSubmit={async (values: any) => {
                  if (workflowForm?.stage?.id) {
                    await patchStage({
                      id: workflowForm?.stage?.id,
                      outcome: workflowForm?.stage.outcome || null,
                      form: {
                        workflow: {
                          ...values,
                          stakeholders: values.stakeholders
                            ?.filter((s: any) => s.contact)
                            ?.map((s: any) =>
                              s.contact?.type === 'organization'
                                ? {
                                    ...s,
                                    contact: null,
                                    organization: { ...s.contact },
                                  }
                                : {
                                    ...s,
                                    contact: {
                                      ...s.contact.contact_r,
                                      organization_r: {
                                        ...s.contact.organization_r,
                                      },
                                    },
                                  }
                            ),
                        },
                      },
                    } as any);
                    await afterSave();
                  }
                }}
                form={workflowForm?.form || []}
              />
            ))}
          </Box>
        )}
      </Box>
    </Container>
  );
};
